import { olderGeneration, youngerGeneration } from '@/assets/messages';
import {
  AnnotatedType,
  PrimaryAnnotatedType,
  SecondaryAnnotatedType,
} from '@/components/annotated-type';
import { StoryQuestion, useStoryQuestion } from '@/components/microstory/question';
import {
  OnStepEnterExitArgs,
  ScrollGraphic,
  ScrollRoot,
  ScrollStep,
  ScrollSteps,
} from '@/components/microstory/scroll';
import { StoryText } from '@/components/microstory/text';
import { SunriseChart, SunriseChartData } from '@/components/sunrise-chart';
import { countryIdToName } from '@/utils/country-id-to-name';
import { useNumberFormatting } from '@/utils/use-number-formatting';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { useState } from 'react';

type GlobalOutlookChapter2Props = {
  data: GlobalOutlookData;
};

export function GlobalOutlookChapter2(props: GlobalOutlookChapter2Props) {
  const { i18n } = useLingui();
  const formatPercentage = useNumberFormatting('percent');

  const [chartData, setChartData] = useState<SunriseChartData>();
  const { selectedAnswer, handleAnswer } = useStoryQuestion();

  const answers = [
    {
      id: 'better',
      label: t({ id: 'global-outlook.question-2.answer-1', message: 'Young are more optimistic' }),
    },
    {
      id: 'worse',
      label: t({ id: 'global-outlook.question-2.answer-2', message: 'Young are more pessimistic' }),
    },
    {
      id: 'no-difference',
      label: t({
        id: 'global-outlook.question-2.answer-3',
        message: 'No difference between the generations',
      }),
    },
  ];

  const getSunriseData = (countryId?: CountryIsoCode) => {
    if (countryId) {
      return [
        {
          label: t({
            id: 'global-outlook.chart-3.label-2',
            message: '% who think the world is becoming a better place {inCountry}',
            values: { inCountry: countryIdToName(countryId, true) },
          }),
          data: [
            {
              label: youngerGeneration(i18n),
              value: props.data.better_place_bd[countryId].younger,
            },
          ],
        },
        {
          data: [
            { label: olderGeneration(i18n), value: props.data.better_place_bd[countryId].older },
          ],
        },
      ];
    }

    return [
      {
        label: t({
          id: 'global-outlook.chart-3.label-1',
          message: '% who think the world is becoming a better place',
        }),
        data: [{ label: youngerGeneration(i18n), value: props.data.better_place.younger.better }],
      },
      {
        data: [{ label: olderGeneration(i18n), value: props.data.better_place.older.better }],
      },
    ];
  };

  const handleStepEnter = async (evt: OnStepEnterExitArgs<SunriseChartData>) => {
    if (evt.data) setChartData(evt.data);
  };

  return (
    <>
      <StoryQuestion
        question={t({
          id: 'global-outlook.question-2.question',
          message:
            'How do you think young and older people compare in terms of optimism, on average?',
        })}
        answers={answers}
        onAnswer={handleAnswer}
        selectedAnswer={selectedAnswer}
        response={
          selectedAnswer?.id === 'better'
            ? t({ id: 'global-outlook.question-2.response-1', message: 'Right!' })
            : t({ id: 'global-outlook.question-2.response-2', message: 'Not quite!' })
        }
      />

      <ScrollRoot>
        <ScrollGraphic>
          <SunriseChart data={chartData} formatValue={formatPercentage} />
        </ScrollGraphic>

        <ScrollSteps onStepEnter={handleStepEnter} offset={0.5} hasGraphic>
          <ScrollStep data={getSunriseData()}>
            <StoryText>
              <Trans id="global-outlook.chapter-2.text-1">
                In all but three countries — {countryIdToName('IN')}, {countryIdToName('MA')}, and{' '}
                {countryIdToName('NG')} — <PrimaryAnnotatedType>young people</PrimaryAnnotatedType>{' '}
                express greater optimism than{' '}
                <SecondaryAnnotatedType>older people.</SecondaryAnnotatedType>
              </Trans>
            </StoryText>
          </ScrollStep>

          <ScrollStep data={getSunriseData()}>
            <StoryText>
              <Trans id="global-outlook.chapter-2.text-2">
                In some countries, the gulf between the generations is yawning.
              </Trans>
            </StoryText>
          </ScrollStep>

          <ScrollStep data={getSunriseData('JP')}>
            <StoryText>
              <Trans id="global-outlook.chapter-2.text-3">
                In <AnnotatedType>{countryIdToName('JP')}</AnnotatedType> the two age groups have
                wildly different views about the world's fortunes.
              </Trans>
            </StoryText>
          </ScrollStep>

          <ScrollStep data={getSunriseData('US')}>
            <StoryText>
              <Trans id="global-outlook.chapter-2.text-4">
                The same is true in the{' '}
                <AnnotatedType>{countryIdToName('US')}&hellip;</AnnotatedType>
              </Trans>
            </StoryText>
          </ScrollStep>

          <ScrollStep data={getSunriseData('AR')}>
            <StoryText>
              <Trans id="global-outlook.chapter-2.text-5">
                &hellip;and <AnnotatedType>{countryIdToName('AR')}.</AnnotatedType>
              </Trans>
            </StoryText>
          </ScrollStep>

          <ScrollStep data={getSunriseData()}>
            <StoryText>
              <Trans id="global-outlook.chapter-2.text-6">
                Overall, with each additional year of age, people are{' '}
                <AnnotatedType>1% less likely</AnnotatedType> to say the world is becoming a better
                place.
              </Trans>
            </StoryText>
          </ScrollStep>

          <ScrollStep data={getSunriseData()}>
            <StoryText>
              <Trans id="global-outlook.chapter-2.text-7">
                Factors other than age also shape people's outlook. People who report{' '}
                <AnnotatedType>facing personal financial struggles</AnnotatedType> are less likely
                to be optimistic about the world. In addition,{' '}
                <AnnotatedType>women are 6% less likely than men</AnnotatedType> to say that the
                world is becoming a better place.
              </Trans>
            </StoryText>
          </ScrollStep>
        </ScrollSteps>
      </ScrollRoot>
    </>
  );
}
